import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Zap, Upload, Copy, Check, X, Loader2, Wifi, WifiOff, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const CHUNK_SIZE = 64 * 1024; // 64KB chunks for WebRTC

const P2PSend = () => {
  const { user, token, isUltimate } = useAuth();
  const { toast } = useToast();
  const [files, setFiles] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [progress, setProgress] = useState(0);
  const [transferComplete, setTransferComplete] = useState(false);
  const [status, setStatus] = useState('select'); // select, waiting, connected, transferring, complete

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) setFiles(selected);
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const createRoom = async () => {
    if (files.length === 0) return;
    try {
      const res = await axios.post(`${API}/p2p/create-room`, {
        filename: files.length === 1 ? files[0].name : `${files.length} fichiers`,
        file_size: totalSize,
        file_count: files.length
      }, { headers: { Authorization: `Bearer ${token}` } });

      setRoomId(res.data.room_id);
      const url = `${window.location.origin}/p2p/${res.data.room_id}`;
      setShareUrl(url);
      setStatus('waiting');
      connectWebSocket(res.data.room_id);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de créer la salle P2P', variant: 'destructive' });
    }
  };

  const iceCandidateQueueRef = useRef([]);
  const negotiatingRef = useRef(false);

  const processIceCandidateQueue = async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    while (iceCandidateQueueRef.current.length > 0) {
      const candidate = iceCandidateQueueRef.current.shift();
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { console.warn('ICE error:', e); }
    }
  };

  const connectWebSocket = useCallback((rid) => {
    const ws = new WebSocket(`${WS_URL}/api/ws/p2p/${rid}/sender`);
    wsRef.current = ws;

    ws.onmessage = async (event) => {
      const msg = JSON.parse(event.data);

      if (msg.type === 'peer-connected' && !negotiatingRef.current) {
        negotiatingRef.current = true;
        setPeerConnected(true);
        setStatus('connected');
        await createPeerConnection(ws);
      }

      if (msg.type === 'answer') {
        const pc = pcRef.current;
        if (pc && pc.signalingState === 'have-local-offer') {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          await processIceCandidateQueue();
        }
      }

      if (msg.type === 'ice-candidate' && msg.candidate) {
        if (pcRef.current && pcRef.current.remoteDescription) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (e) { console.warn('ICE error:', e); }
        } else {
          iceCandidateQueueRef.current.push(msg.candidate);
        }
      }

      if (msg.type === 'peer-disconnected') {
        setPeerConnected(false);
        if (!transferComplete) {
          negotiatingRef.current = false;
          setStatus('waiting');
        }
      }
    };

    ws.onclose = () => {
      if (!transferComplete) setPeerConnected(false);
    };
  }, [transferComplete]);

  const createPeerConnection = async (ws) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' }
      ]
    });
    pcRef.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        ws.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate }));
      }
    };

    // Create data channel
    const dc = pc.createDataChannel('fileTransfer', { ordered: true });
    dc.binaryType = 'arraybuffer';
    dcRef.current = dc;

    dc.onopen = () => {
      setStatus('transferring');
      setTransferring(true);
      sendFiles(dc, ws);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    ws.send(JSON.stringify({ type: 'offer', sdp: pc.localDescription }));
  };

  const sendFiles = async (dc, ws) => {
    let totalSent = 0;
    const totalBytes = totalSize;

    for (let fileIdx = 0; fileIdx < files.length; fileIdx++) {
      const file = files[fileIdx];

      // Send file metadata
      dc.send(JSON.stringify({
        type: 'file-meta',
        name: file.name,
        size: file.size,
        mimeType: file.type,
        index: fileIdx,
        total: files.length
      }));

      // Wait for metadata to be processed
      await new Promise(r => setTimeout(r, 100));

      // Read and send file in chunks
      const reader = file.stream().getReader();
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          // Split into smaller chunks for DataChannel
          for (let offset = 0; offset < value.length; offset += CHUNK_SIZE) {
            const chunk = value.slice(offset, offset + CHUNK_SIZE);

            // Wait if buffer is full
            while (dc.bufferedAmount > 4 * 1024 * 1024) {
              await new Promise(r => setTimeout(r, 50));
            }

            dc.send(chunk);
            totalSent += chunk.length;
            const pct = Math.round((totalSent / totalBytes) * 100);
            setProgress(pct);

            ws.send(JSON.stringify({ type: 'transfer-progress', progress: pct }));
          }
        }
      }

      // Signal end of file
      dc.send(JSON.stringify({ type: 'file-end', index: fileIdx }));
      await new Promise(r => setTimeout(r, 200));
    }

    // Signal transfer complete
    dc.send(JSON.stringify({ type: 'transfer-done' }));
    ws.send(JSON.stringify({ type: 'transfer-complete' }));
    setTransferComplete(true);
    setTransferring(false);
    setStatus('complete');
    setProgress(100);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({ title: 'Lien copié !' });
    setTimeout(() => setCopied(false), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} Mo`;
    return `${(bytes / 1073741824).toFixed(2)} Go`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      pcRef.current?.close();
    };
  }, []);

  if (!isUltimate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
        <NavigationMenu />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <Zap className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Transfert P2P</h1>
            <p className="text-blue-300/60 mb-6">Le transfert Peer-to-Peer est disponible exclusivement avec la formule Ultimate</p>
            <Button onClick={() => window.location.href = '/pricing'} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Zap className="w-4 h-4 mr-2" /> Découvrir Ultimate
            </Button>
          </div>
        </main>
        <DynamicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950/30 to-slate-900 flex flex-col">
      <NavigationMenu />
      <Toaster />

      <main className="flex-1 pt-28 pb-20 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-300 text-sm mb-4">
              <Zap className="w-4 h-4" /> Transfert P2P
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Envoyer en direct</h1>
            <p className="text-blue-300/60 text-sm">Transfert direct navigateur-à-navigateur, sans passer par nos serveurs</p>
          </div>

          <div className="bg-slate-800/50 border border-purple-500/20 rounded-2xl p-6 space-y-6">

            {status === 'select' && (
              <>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-purple-500/30 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500/50 hover:bg-purple-500/5 transition-all"
                  data-testid="p2p-file-drop"
                >
                  <Upload className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">Sélectionnez vos fichiers</p>
                  <p className="text-sm text-blue-300/50">Aucune limite de taille en P2P</p>
                  <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" data-testid="p2p-file-input" />
                </div>

                {files.length > 0 && (
                  <div className="space-y-3">
                    <div className="bg-slate-900/50 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                      {files.map((f, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-white truncate mr-2">{f.name}</span>
                          <span className="text-blue-300/50 shrink-0">{formatSize(f.size)}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-blue-300/50 text-right">{files.length} fichier(s) - {formatSize(totalSize)}</p>
                    <Button onClick={createRoom} className="w-full bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold" data-testid="p2p-create-room-btn">
                      <Zap className="w-4 h-4 mr-2" /> Créer le lien P2P
                    </Button>
                  </div>
                )}
              </>
            )}

            {status === 'waiting' && (
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center gap-2 text-orange-400">
                  <WifiOff className="w-5 h-5" />
                  <span className="font-medium">En attente du destinataire...</span>
                </div>
                <div className="space-y-3">
                  <p className="text-sm text-blue-300/50">Partagez ce lien avec le destinataire</p>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={shareUrl} className="flex-1 bg-slate-900/50 border border-purple-500/20 rounded-lg px-4 py-2.5 text-white text-sm font-mono truncate focus:outline-none" data-testid="p2p-share-url" />
                    <Button onClick={copyUrl} className="bg-purple-600 hover:bg-purple-700 text-white shrink-0" data-testid="p2p-copy-url">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex justify-center pt-4">
                    <div className="bg-white p-3 rounded-xl">
                      <QRCodeSVG value={shareUrl} size={140} level="H" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {status === 'connected' && (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 text-emerald-400">
                  <Wifi className="w-5 h-5" />
                  <span className="font-medium">Destinataire connecté !</span>
                </div>
                <p className="text-sm text-blue-300/50">Établissement de la connexion P2P...</p>
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
              </div>
            )}

            {status === 'transferring' && (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 text-purple-400">
                  <Zap className="w-5 h-5 animate-pulse" />
                  <span className="font-medium">Transfert P2P en cours...</span>
                </div>
                <div className="relative w-full bg-slate-900/50 rounded-full h-4 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-300 rounded-full" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-white font-bold text-2xl">{progress}%</p>
                <p className="text-sm text-blue-300/50">Les fichiers sont transférés directement sans passer par nos serveurs</p>
              </div>
            )}

            {status === 'complete' && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Transfert terminé !</h2>
                <p className="text-sm text-blue-300/50">{files.length} fichier(s) envoyé(s) - {formatSize(totalSize)}</p>
                <Button onClick={() => { setFiles([]); setRoomId(null); setStatus('select'); setProgress(0); setTransferComplete(false); }} variant="outline" className="border-purple-500/30 text-purple-300">
                  Nouveau transfert
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
      <DynamicFooter />
    </div>
  );
};

export default P2PSend;
